/**
 * 头像的三个端点：改成 emoji（`PATCH /me`）、上传自定义图片（`POST /me/avatar`）、
 * 撤回自定义图片（`DELETE /me/avatar`）。
 *
 * 和 `emailAuthApi.ts` 是同一套分层，但收尾不一样：这里**不换身份**，
 * 三个端点都返回同一个账号的最新资料，成功后调 `auth.updateAccount()` 就够了——
 * 不需要 `adopt()` 那一整套令牌轮换与身份代次。
 */
import type { PublicAccount, VoiceId } from '@poker/shared'

import { getAuthSession } from './useAuth.js'
import { AuthError, parseJsonBody, readApiError, type AuthSession } from '../auth/session.js'
import { bumpOwnAvatarVersion, emojiAvatarSeed } from '../table/avatar.js'

export type ProfileFailureKind = 'network' | 'rate-limited' | 'invalid' | 'not-authenticated' | 'server'

export class ProfileError extends Error {
  constructor(
    override readonly message: string,
    readonly kind: ProfileFailureKind,
  ) {
    super(message)
    this.name = 'ProfileError'
  }
}

export interface ProfileApiOptions {
  readonly auth?: AuthSession
  readonly fetchImpl?: typeof fetch
}

function describeProfileFailure(kind: ProfileFailureKind): string {
  switch (kind) {
    case 'network':
      return '网络没连上，检查一下再试'
    case 'rate-limited':
      return '试过太多次了，等一会儿再试'
    case 'invalid':
      return '图片格式不支持，或者文件太大'
    case 'not-authenticated':
      return '本机的登录状态过期了，刷新一下页面再来'
    default:
      return '服务器出了点问题，再试一次'
  }
}

function classify(err: AuthError): ProfileFailureKind {
  if (err.code === 'E_NETWORK' || err.status === 0) return 'network'
  if (err.status === 429 || err.code === 'E_RATE_LIMITED') return 'rate-limited'
  if (err.status === 401) return 'not-authenticated'
  if (err.status === 400 || err.code === 'E_AVATAR_INVALID') return 'invalid'
  return 'server'
}

function toProfileError(e: unknown): ProfileError {
  if (e instanceof ProfileError) return e
  const err = e instanceof AuthError ? e : new AuthError(String(e), 'E_NETWORK', 0, false)
  const kind = classify(err)
  return new ProfileError(describeProfileFailure(kind), kind)
}

interface RequestInput {
  readonly auth: AuthSession
  readonly fetchImpl: typeof fetch
  readonly method: 'PATCH' | 'POST' | 'DELETE'
  readonly path: string
  readonly body?: unknown
}

async function request(input: RequestInput): Promise<{ account?: PublicAccount } | null> {
  const token = await input.auth.ensureAccessToken()
  let res: Response
  try {
    res = await input.fetchImpl(`${input.auth.baseUrl()}/api${input.path}`, {
      method: input.method,
      headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
      ...(input.body === undefined ? {} : { body: JSON.stringify(input.body) }),
    })
  } catch (e) {
    throw new AuthError(`网络不可用：${String(e)}`, 'E_NETWORK', 0, false)
  }
  const parsed = parseJsonBody(await res.text())
  if (!res.ok) throw readApiError(res.status, parsed)
  return parsed as { account?: PublicAccount } | null
}

function resolve(options: ProfileApiOptions): { auth: AuthSession; fetchImpl: typeof fetch } {
  return {
    auth: options.auth ?? getAuthSession(),
    fetchImpl: options.fetchImpl ?? ((...args) => globalThis.fetch(...args)),
  }
}

function landAccount(auth: AuthSession, body: { account?: PublicAccount } | null): PublicAccount {
  if (body?.account == null) throw new ProfileError(describeProfileFailure('server'), 'server')
  // ★ 请求飞行期间换了号（登录落地在响应之前）：这是上一个账号的资料，不能把当前账号改回去
  if (auth.account()?.id === body.account.id) auth.updateAccount(body.account)
  return body.account
}

/**
 * 把昵称存到账号上。
 *
 * ★ 以前昵称只活在这台设备的 localStorage 里，从没上传过 —— 于是换设备、清数据、
 *   或者在别的手机上登录，拿到的都是注册时服务端生成的 `Player-XXXX`。
 *   「我是谁」这件事必须跟着账号走，不是跟着浏览器走。
 *
 * ★ 尽力而为：失败了不打扰用户。本地那份仍然有效 —— 建房/进房时昵称是随消息带上去的，
 *   这次没存成，下次失焦再存一次就行。
 */
export async function saveNickname(nickname: string, options: ProfileApiOptions = {}): Promise<PublicAccount> {
  const { auth, fetchImpl } = resolve(options)
  const clean = nickname.trim()
  if (clean === '') throw new ProfileError('昵称不能是空的', 'invalid')
  try {
    const body = await request({ auth, fetchImpl, method: 'PATCH', path: '/me', body: { nickname: clean } })
    return landAccount(auth, body)
  } catch (e) {
    throw toProfileError(e)
  }
}

/** 选短语语音的声音（男声 / 女声）。 */
export async function saveVoice(voice: VoiceId, options: ProfileApiOptions = {}): Promise<PublicAccount> {
  const { auth, fetchImpl } = resolve(options)
  try {
    const body = await request({ auth, fetchImpl, method: 'PATCH', path: '/me', body: { voice } })
    return landAccount(auth, body)
  } catch (e) {
    throw toProfileError(e)
  }
}

/** 选一个 emoji 当头像。 */
export async function setEmojiAvatar(char: string, options: ProfileApiOptions = {}): Promise<PublicAccount> {
  const { auth, fetchImpl } = resolve(options)
  try {
    const body = await request({ auth, fetchImpl, method: 'PATCH', path: '/me', body: { avatarSeed: emojiAvatarSeed(char) } })
    return landAccount(auth, body)
  } catch (e) {
    throw toProfileError(e)
  }
}

/** 退回默认的字母头像（不是撤回自定义图片——那是 `removeCustomAvatar`）。 */
export async function resetAvatarToDefault(options: ProfileApiOptions = {}): Promise<PublicAccount> {
  const { auth, fetchImpl } = resolve(options)
  try {
    const body = await request({ auth, fetchImpl, method: 'PATCH', path: '/me', body: { avatarSeed: '' } })
    return landAccount(auth, body)
  } catch (e) {
    throw toProfileError(e)
  }
}

/** 上传一张自定义头像。`dataUrl` 应该已经过客户端 Canvas 压缩（见 avatarUpload.ts）。 */
export async function uploadCustomAvatar(dataUrl: string, options: ProfileApiOptions = {}): Promise<PublicAccount> {
  const { auth, fetchImpl } = resolve(options)
  try {
    const body = await request({ auth, fetchImpl, method: 'POST', path: '/me/avatar', body: { dataUrl } })
    // ★ 地址不变、字节变了：不 bump 的话页面上那个 <img> 的 src 一模一样，
    //   React 不改属性、浏览器不重发请求，用户看到的还是上一张（见 table/avatar.ts）。
    bumpOwnAvatarVersion()
    return landAccount(auth, body)
  } catch (e) {
    throw toProfileError(e)
  }
}

/** 撤回自定义头像，退回默认的字母头像。 */
export async function removeCustomAvatar(options: ProfileApiOptions = {}): Promise<PublicAccount> {
  const { auth, fetchImpl } = resolve(options)
  try {
    const body = await request({ auth, fetchImpl, method: 'DELETE', path: '/me/avatar' })
    return landAccount(auth, body)
  } catch (e) {
    throw toProfileError(e)
  }
}
